import React from 'react';
import { Card, Tag, Typography, Space, Divider, Empty, Flex } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import { LotteryResultsResponse, PrizeSummary } from '../../types/engagement.types';

const { Text, Title } = Typography;

const RANK_LABELS: Record<number, string> = {
  1: '1º Prêmio',
  2: '2º Prêmio',
  3: '3º Prêmio',
  4: '4º Prêmio',
  5: '5º Prêmio',
};

interface LotteryResultsCardProps {
  results: LotteryResultsResponse;
  participantTicketNumbers?: string[];
}

export const LotteryResultsCard: React.FC<LotteryResultsCardProps> = ({
  results,
  participantTicketNumbers = [],
}) => {
  const hasAnyWinner = results.prizes.some((p) => p.hasWinner);

  return (
    <Card
      title={
        <Space>
          <TrophyOutlined style={{ color: '#faad14' }} />
          <span>Resultados do Sorteio — Ciclo {results.cycleKey}</span>
        </Space>
      }
    >
      {results.lotteryDrawNumber && (
        <Text type="secondary">
          Concurso nº <Text strong>{results.lotteryDrawNumber}</Text>
          {results.lotteryDrawDate && (
            <> · {new Date(results.lotteryDrawDate).toLocaleDateString('pt-BR')}</>
          )}
        </Text>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {!hasAnyWinner ? (
        <Empty description="Resultados ainda não processados" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Space
          direction="vertical"
          size={0}
          split={<Divider style={{ margin: '10px 0' }} />}
          style={{ width: '100%' }}
        >
          {results.prizes.map((prize: PrizeSummary) => {
            const isWinner =
              prize.winningTicketNumber != null &&
              participantTicketNumbers.some((tn) => tn === prize.winningTicketNumber);

            return (
              <div key={prize.prizeRank}>
                <Flex justify="space-between" align="center" gap={8} wrap="wrap">
                  <Space size={8}>
                    <Title level={5} style={{ margin: 0 }}>
                      {RANK_LABELS[prize.prizeRank] ?? `${prize.prizeRank}º Prêmio`}
                    </Title>
                    <Text strong style={{ color: '#00984A' }}>
                      {prize.prizeValue}
                    </Text>
                  </Space>
                  {isWinner && <Tag color="gold">🎉 Seu bilhete!</Tag>}
                  {!prize.winningTicketNumber && <Tag>Sem ganhador</Tag>}
                </Flex>

                {(prize.drawnNumber || prize.winningTicketNumber) && (
                  <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                    {prize.drawnNumber && (
                      <>
                        Sorteado <Text code>{prize.drawnNumber}</Text>
                      </>
                    )}
                    {prize.drawnNumber && prize.winningTicketNumber && '   '}
                    {prize.winningTicketNumber && (
                      <>
                        Bilhete{' '}
                        <Text code style={isWinner ? { background: '#fffbe6', fontWeight: 'bold' } : undefined}>
                          {prize.winningTicketNumber}
                        </Text>
                      </>
                    )}
                  </Text>
                )}

                {(prize.winnerName || prize.winnerEmail) && (
                  <Text type="secondary" style={{ display: 'block' }}>
                    Ganhador: {prize.winnerName}
                    {prize.winnerName && prize.winnerEmail && ' · '}
                    {prize.winnerEmail}
                  </Text>
                )}
              </div>
            );
          })}
        </Space>
      )}
    </Card>
  );
};

export default LotteryResultsCard;
